import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AssetManagement, Header } from '@aptsi-types';
import { AssetsViewService } from '../shared/services/assets-view.service';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DataAssetManualUpdateComponent } from './data-asset-manual-update/data-asset-manual-update.component';
import { SystemAssetManualUpdateComponent } from './system-asset-manual-update/system-asset-manual-update.component';

type AssetTab = 'system' | 'data';

type AssetRecord = AssetManagement & Record<string, unknown>;

interface AssetPaginationMeta {
  totalSystemAssets?: number;
  totalDataAssets?: number;
  totalAssets?: number;
  totalPages?: number;
  currentPage?: number;
}

interface NormalizedAssetResponse {
  rows: AssetManagement[];
  metadata?: AssetPaginationMeta;
}

interface AssetListState {
  currentPage: number;
  totalPagesNormal: number;
  totalPagesSearch: number;
  totalItemsNormal: number;
  totalItemsSearch: number;
  isLoading: boolean;
  isSearching: boolean;
  loadError: boolean;
  searchError: boolean;
  issearch: boolean;
  loaded: boolean;
  searchText: string;
  filterByHeader: string;
  filteredData: AssetManagement[];
  searchedData: AssetManagement[];
  requestSequence: number;
}

function createAssetListState(initiallyLoading = false): AssetListState {
  return {
    currentPage: 1,
    totalPagesNormal: 0,
    totalPagesSearch: 0,
    totalItemsNormal: 0,
    totalItemsSearch: 0,
    isLoading: initiallyLoading,
    isSearching: false,
    loadError: false,
    searchError: false,
    issearch: false,
    loaded: false,
    searchText: '',
    filterByHeader: 'all',
    filteredData: [],
    searchedData: [],
    requestSequence: 0
  };
}

@Component({
  selector: 'app-assets-view',
  templateUrl: './assets-view.component.html',
  styleUrls: ['./assets-view.component.scss']
})
export class AssetsViewComponent implements OnInit {
  readonly itemsPerPage = 10;

  isVerifying = false;
  verificationSucceeded = false;
  verificationMessage = '';

  @ViewChild('verificationResultDialog', { static: true })
  private verificationResultDialog!: TemplateRef<unknown>;

  private verificationDialogRef: MatDialogRef<unknown, unknown> | null = null;


readonly buttonConfig = (row: unknown): Record<string, boolean> => {
    // The shared table can pass a display row or a row wrapper depending on
    // its renderer. Both forms are supported by readManualFlag().
    const isManual = this.readManualFlag(row);

    return {
      update: isManual,
      delete: false,
      import: false,
      configure: false,
      view: false,
    };
  };

  activeTab: AssetTab = 'system';

  readonly systemState = createAssetListState(true);
  readonly dataState = createAssetListState(false);

  private cachedTableSource: AssetManagement[] | null = null;
  private cachedTableTab: AssetTab | null = null;
  private cachedTableData: AssetManagement[] = [];

  readonly systemHeaders: Header[] = [
    { name: 'Asset Name', property: 'systemAssetName', showInDropdown: true, description: 'Name of the system asset' },
    { name: 'Description', property: 'systemAssetDescription', showInDropdown: true, description: 'Description of the system asset' },
    { name: 'Owner', property: 'systemAssetOwner', showInDropdown: true, description: 'Owner or responsible team' },
    { name: 'Status', property: 'systemAssetStatus', showInDropdown: true, description: 'Current asset status' },
    { name: 'Is Manual', property: 'isManual', showInDropdown: true, description: 'Whether the asset is manually configured' },
    { name: 'Lifecycle', property: 'systemAssetLifecycleStatus', showInDropdown: true, description: 'System asset lifecycle status' },
    { name: 'Endpoint', property: 'systemAssetEndpoint', showInDropdown: true, description: 'System asset endpoint' },
    { name: 'Business Value', property: 'systemAssetBusinessValue', showInDropdown: true, description: 'Business value rating' },
    { name: 'Classification', property: 'dataClassification', showInDropdown: true, description: 'Data classification' },
    { name: 'Asset Source', property: 'assetSourceName', showInDropdown: true, description: 'Source from which the asset was discovered' },
    { name: 'Imported At', property: 'importedAt', showInDropdown: true, description: 'Import timestamp' },
    { name: 'Unique Identifier', property: 'uniqueIdentifier', showInDropdown: true, description: 'Unique asset identifier' }
  ];

  readonly dataHeaders: Header[] = [
    { name: 'Data Asset Name', property: 'name', showInDropdown: true, description: 'Name of the data asset' },
    { name: 'Description', property: 'description', showInDropdown: true, description: 'Description of the data asset' },
    { name: 'Technical Name', property: 'assetTechnicalName', showInDropdown: true, description: 'Technical asset type or name' },
    { name: 'Qualified Name', property: 'qualifiedName', showInDropdown: true, description: 'Fully qualified asset name' },
    { name: 'Is Manual', property: 'manual', showInDropdown: true, description: 'Whether the asset is manually configured' },
    { name: 'Status', property: 'entityStatus', showInDropdown: true, description: 'Current data asset status' },
    { name: 'Classification', property: 'classificationName', showInDropdown: true, description: 'Assigned classifications' },
    { name: 'Asset Source', property: 'assetsourceName', showInDropdown: true, description: 'Source from which the data asset was discovered' },
    { name: 'Collection', property: 'collectionId', showInDropdown: true, description: 'Collection identifier' },
    { name: 'Imported By', property: 'importedBy', showInDropdown: true, description: 'User who imported the asset' },
    { name: 'Imported At', property: 'importedAt', showInDropdown: true, description: 'Import timestamp' },
    { name: 'Unique Identifier', property: 'uniqueIdentifier', showInDropdown: true, description: 'Unique asset identifier' }
  ];

  constructor(
    private readonly assetsViewService: AssetsViewService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly router: Router,
    private readonly dialog: MatDialog
  ) { }

  ngOnInit(): void {
    void this.fetchAssets('system');
  }

  handleAction(event: {
    action: string;
    rowIndex: number;
    rowData: AssetManagement;
  }): void {
    switch (event.action) {
      case 'update':
        // Use the emitted row instead of indexing filteredData. This also
        // works when the table is displaying server-side search results.
        this.updateManualAsset(event.rowData, event.rowIndex);
        break;
      case 'delete':
        void this.deleteAsset(event.rowIndex);
        break;
      default:
        break;
    }
  }

  updateManualAsset(rowData: AssetManagement, rowIndex: number): void {
    const displayedRow = rowData as AssetRecord;
    const fallbackRow = this.displayedData[rowIndex] as AssetRecord | undefined;
    const asset = displayedRow ?? fallbackRow;

    if (!asset) {
      console.error('[AssetsView] Unable to resolve the selected asset row for update.');
      return;
    }

    if (this.activeTab === 'system') {
      const systemAssetId = String(
        asset['systemAssetId'] ?? asset['systemassetid'] ?? ''
      ).trim();

      if (!systemAssetId) {
        console.error('[AssetsView] System Asset ID is missing from the selected row.', asset);
        return;
      }

      const dialogRef = this.dialog.open(SystemAssetManualUpdateComponent, {
        data: { assetId: systemAssetId },
        width: 'min(960px, 96vw)',
        maxWidth: '96vw',
        maxHeight: '92vh',
        autoFocus: 'first-tabbable',
        restoreFocus: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.updated) {
          void this.fetchAssets('system');
        }
      });

      return;
    }

    const dataAssetId = String(
      asset['assetId'] ??
      asset['dataAssetId'] ??
      asset['dataassetid'] ??
      asset['uniqueIdentifier'] ??
      ''
    ).trim();

    if (!dataAssetId) {
      console.error('[AssetsView] Data Asset ID is missing from the selected row.', asset);
      return;
    }

    const dialogRef = this.dialog.open(DataAssetManualUpdateComponent, {
      data: { assetId: dataAssetId },
      width: 'min(1000px, 96vw)',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: 'first-tabbable',
      restoreFocus: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.updated) {
        void this.fetchAssets('data');
      }
    });
  }

  deleteAsset(rowIndex: number): void {

  }

  get currentState(): AssetListState {
    return this.activeTab === 'system' ? this.systemState : this.dataState;
  }

  get currentHeaders(): Header[] {
    return this.activeTab === 'system' ? this.systemHeaders : this.dataHeaders;
  }

  get displayedData(): AssetManagement[] {
    const state = this.currentState;
    return state.issearch ? state.searchedData : state.filteredData;
  }

  /**
   * app-main-table reads each cell through header.property. Map each record to
   * those properties while retaining the existing aliases and display formatting.
   */
  get tableData(): AssetManagement[] {
    const tab = this.activeTab;
    const source = this.displayedData;

    // Keep a stable array reference between data changes so the shared table
    // does not receive a newly-created data array on every change-detection pass.
    if (source === this.cachedTableSource && tab === this.cachedTableTab) {
      return this.cachedTableData;
    }

    const headers = this.currentHeaders;
    this.cachedTableData = source.map((asset) => {
      const row: Record<string, unknown> = {
        ...(asset as unknown as Record<string, unknown>)
      };

      for (const header of headers) {
        row[header.property] = this.getCellValue(asset, header.property, tab);
      }

      // Preserve the manual flag in a canonical property after display-cell
      // mapping. In particular, the Data Assets header is named "manual",
      // while some API versions return the field as "isManual".
      const isManual = this.readManualFlag(asset);
      row['__isManualAsset'] = isManual;
      row['isManual'] = isManual;
      row['manual'] = isManual;

      return row as unknown as AssetManagement;
    });

    this.cachedTableSource = source;
    this.cachedTableTab = tab;
    return this.cachedTableData;
  }

  get currentTotalPages(): number {
    return this.currentState.issearch ? this.currentState.totalPagesSearch : this.currentState.totalPagesNormal;
  }

  get currentTotalItems(): number {
    return this.currentState.issearch ? this.currentState.totalItemsSearch : this.currentState.totalItemsNormal;
  }

  get currentTabLabel(): string {
    return this.activeTab === 'system' ? 'system assets' : 'data assets';
  }

  switchTab(tab: AssetTab): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    const state = this.stateFor(tab);
    if (!state.loaded && !state.isLoading) void this.fetchAssets(tab);
  }

  async fetchAssets(tab: AssetTab = this.activeTab): Promise<void> {
    const state = this.stateFor(tab);
    if (state.issearch && state.searchText.trim()) {
      await this.fetchAssetsSearch(tab);
      return;
    }

    const requestId = ++state.requestSequence;
    state.isLoading = true;
    state.isSearching = false;
    state.loadError = false;
    state.searchError = false;

    try {
      const apiResponse: unknown = tab === 'system'
        ? await this.assetsViewService.FetchSystemAssetsView(state.currentPage - 1).toPromise()
        : await this.assetsViewService.FetchDataAssetsView(state.currentPage - 1).toPromise();

      // Do not let an older request overwrite results from a later page/tab action.
      if (requestId !== state.requestSequence) return;

      const normalized = this.normalizeResponse(apiResponse);
      const metadata = normalized.metadata ?? this.findMetadata(normalized.rows);
      const total = tab === 'system'
        ? metadata?.totalSystemAssets ?? metadata?.totalAssets
        : metadata?.totalDataAssets ?? metadata?.totalAssets;

      state.totalItemsNormal = this.readNonNegativeNumber(total, normalized.rows.length);
      // Backend metadata is authoritative. Do not calculate page totals in the UI.
      state.totalPagesNormal = this.readNonNegativeNumber(metadata?.totalPages, 0);
      state.loaded = true;

      if (state.totalPagesNormal === 0) {
        state.currentPage = 1;
      } else if (state.currentPage > state.totalPagesNormal) {
        // If the result count shrank while browsing, request the last valid page.
        state.currentPage = state.totalPagesNormal;
        await this.fetchAssets(tab);
        return;
      }

      state.filteredData = normalized.rows;
      this.cachedTableSource = null;
      console.debug(`[AssetsView] ${tab} rows loaded:`, state.filteredData.length);
    } catch (error) {
      if (requestId !== state.requestSequence) return;

      console.error(`Error fetching ${tab} assets:`, error);
      state.loadError = true;
      state.loaded = false;
      state.filteredData = [];
      state.totalItemsNormal = 0;
      state.totalPagesNormal = 0;
      state.currentPage = 1;
      this.cachedTableSource = null;
    } finally {
      if (requestId === state.requestSequence) {
        state.isLoading = false;
        state.isSearching = false;
        this.refreshView();
      }
    }
  }

  async searchAssets(tab: AssetTab = this.activeTab): Promise<void> {
    const state = this.stateFor(tab);
    const query = state.searchText.trim();
    if (!query) {
      await this.clearSearch(tab);
      return;
    }
    state.searchText = query;
    state.issearch = true;
    state.currentPage = 1;
    state.searchedData = [];
    state.totalItemsSearch = 0;
    state.totalPagesSearch = 0;
    this.cachedTableSource = null;
    await this.fetchAssetsSearch(tab);
  }

  async fetchAssetsSearch(tab: AssetTab = this.activeTab): Promise<void> {
    const state = this.stateFor(tab);
    if (!state.searchText.trim()) {
      await this.clearSearch(tab);
      return;
    }

    const requestId = ++state.requestSequence;
    state.isSearching = true;
    state.isLoading = false;
    state.searchError = false;

    try {
      const apiResponse: unknown = tab === 'system'
        ? await this.assetsViewService.FetchSystemAssetsViewSearch(
          state.currentPage - 1, state.searchText.trim(), state.filterByHeader
        ).toPromise()
        : await this.assetsViewService.FetchDataAssetsViewSearch(
          state.currentPage - 1, state.searchText.trim(), state.filterByHeader
        ).toPromise();

      // A newer search/page request makes this response stale.
      if (requestId !== state.requestSequence) return;

      const normalized = this.normalizeResponse(apiResponse);
      const metadata = normalized.metadata ?? this.findMetadata(normalized.rows);
      const total = tab === 'system'
        ? metadata?.totalSystemAssets ?? metadata?.totalAssets
        : metadata?.totalDataAssets ?? metadata?.totalAssets;

      // The endpoint already filters and paginates the search results.
      state.searchedData = normalized.rows;
      state.totalItemsSearch = this.readNonNegativeNumber(total, normalized.rows.length);
      // Use the backend's totalPages value directly for search pagination too.
      state.totalPagesSearch = this.readNonNegativeNumber(metadata?.totalPages, 0);
      state.loaded = true;

      if (state.totalPagesSearch === 0) {
        state.currentPage = 1;
      } else if (state.currentPage > state.totalPagesSearch) {
        state.currentPage = state.totalPagesSearch;
        await this.fetchAssetsSearch(tab);
        return;
      }

      this.cachedTableSource = null;
      console.debug(`[AssetsView] ${tab} search rows loaded:`, state.searchedData.length);
    } catch (error) {
      if (requestId !== state.requestSequence) return;

      console.error(`Error searching ${tab} assets:`, error);
      state.searchError = true;
      state.searchedData = [];
      state.totalItemsSearch = 0;
      state.totalPagesSearch = 0;
      state.currentPage = 1;
      this.cachedTableSource = null;
    } finally {
      if (requestId === state.requestSequence) {
        state.isSearching = false;
        state.isLoading = false;
        this.refreshView();
      }
    }
  }

  async clearSearch(tab: AssetTab = this.activeTab): Promise<void> {
    const state = this.stateFor(tab);
    state.searchText = '';
    state.issearch = false;
    state.searchError = false;
    state.currentPage = 1;
    state.searchedData = [];
    state.totalItemsSearch = 0;
    state.totalPagesSearch = 0;
    this.cachedTableSource = null;
    await this.fetchAssets(tab);
  }

  onInputChange(event: Event): void {
    this.currentState.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    const state = this.currentState;
    state.currentPage = 1;
    state.searchError = false;
    if (state.searchText.trim()) {
      state.issearch = true;
      state.searchedData = [];
      state.totalItemsSearch = 0;
      state.totalPagesSearch = 0;
      this.cachedTableSource = null;
      void this.fetchAssetsSearch(this.activeTab);
    } else {
      state.issearch = false;
      void this.fetchAssets(this.activeTab);
    }
  }

  changePage(page: number): void {
    const state = this.currentState;
    const activeTotalPages = state.issearch ? state.totalPagesSearch : state.totalPagesNormal;

    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > activeTotalPages ||
      page === state.currentPage
    ) {
      return;
    }

    state.currentPage = page;
    if (state.issearch) void this.fetchAssetsSearch(this.activeTab);
    else void this.fetchAssets(this.activeTab);
  }

  retryCurrentTab(): void {
    void this.fetchAssets(this.activeTab);
  }

  trackByAssetId = (index: number, asset: AssetManagement): string | number => {
    const record = asset as AssetRecord;
    return String(record['systemAssetId'] ?? record['assetId'] ?? record['uniqueIdentifier'] ?? index);
  };

  /**
   * Reads manual-asset flags without assuming one API property name or type.
   * Both keys are inspected independently: an explicit false in one alias
   * must not mask a true value in another alias.
   */
  private readManualFlag(source: unknown, depth = 0): boolean {
    if (depth > 3 || source === null || typeof source !== 'object') {
      return false;
    }

    const record = source as Record<string, unknown>;
    const manualKeys = new Set([
      'ismanual',
      'manual',
      'is_manual',
      'manualflag',
      '__ismanualasset'
    ]);

    const isTruthyManualValue = (value: unknown): boolean => {
      if (value === true || value === 1) {
        return true;
      }

      const normalized = String(value ?? '').trim().toLowerCase();
      return ['true', '1', 'yes', 'y', 'manual', 'manually configured'].includes(normalized);
    };

    // Case-insensitive key comparison handles isManual, ismanual, IS_MANUAL, etc.
    for (const [key, value] of Object.entries(record)) {
      if (manualKeys.has(key.replace(/[-\s]/g, '').toLowerCase()) &&
          isTruthyManualValue(value)) {
        return true;
      }
    }

    // Handle common table/renderer wrapper objects without changing the raw record.
    for (const nestedKey of ['rowData', 'data', 'asset', 'record']) {
      const nested = record[nestedKey];
      if (nested && typeof nested === 'object' &&
          this.readManualFlag(nested, depth + 1)) {
        return true;
      }
    }

    return false;
  }

  getCellValue(asset: AssetManagement, property: string, tab: AssetTab = this.activeTab): string {
    const systemAliases: Record<string, string[]> = {
      systemAssetName: ['systemAssetName', 'assetName', 'name'],
      systemAssetDescription: ['systemAssetDescription', 'assetDescription', 'description'],
      systemAssetOwner: ['systemAssetOwner', 'assetOwner', 'owner'],
      systemAssetStatus: ['systemAssetStatus', 'assetStatus', 'status'],
      systemAssetLifecycleStatus: ['systemAssetLifecycleStatus', 'assetLifeCycleStatus', 'lifecycleStatus'],
      systemAssetEndpoint: ['systemAssetEndpoint', 'assetEndPoint', 'endpoint', 'url'],
      systemAssetBusinessValue: ['systemAssetBusinessValue', 'businessValue'],
      dataClassification: ['dataClassification', 'classificationName'],
      assetSourceName: ['assetSourceName', 'assetsourceName'],
      assetSource: ['assetSource', 'assetsource']
    };

    const dataAliases: Record<string, string[]> = {
      name: ['name', 'dataAssetName', 'DATAAssetName', 'assetName'],
      description: ['description', 'dataAssetDescription', 'DATAAssetDescription', 'assetDescription'],
      assetTechnicalName: ['assetTechnicalName', 'technicalName'],
      qualifiedName: ['qualifiedName', 'uniqueIdentifier'],
      entityStatus: ['entityStatus', 'status'],
      classificationName: ['classificationName', 'dataClassification'],
      assetsourceName: ['assetsourceName', 'assetSourceName'],
      assetsource: ['assetsource', 'assetSource'],
      importedBy: ['importedBy', 'createdBy'],
      collectionId: ['collectionId'],
      importedAt: ['importedAt'],
      uniqueIdentifier: ['uniqueIdentifier', 'assetId', 'dataAssetId'],
      manual: ['manual', 'isManual', 'ismanual', 'is_manual']
    };

    const record = asset as AssetRecord;
    const aliases = (tab === 'system' ? systemAliases[property] : dataAliases[property]) || [property];
    let value: unknown;
    for (const alias of aliases) {
      const candidate = record[alias];
      if (candidate !== null && candidate !== undefined && String(candidate).trim() !== '') {
        value = candidate;
        break;
      }
    }

    if (value === null || value === undefined || String(value).trim() === '') return '—';
    if (property === 'classificationName' || property === 'dataClassification') return this.formatClassification(value);

    if (['importedAt', 'updatedAt', 'creationTimestamp', 'lastModifiedTimestamp'].includes(property)) {
      const date = new Date(String(value));
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString(undefined, {
          day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'
        });
      }
    }
    if (Array.isArray(value)) return value.map(item => String(item)).join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  isStatusColumn(property: string): boolean {
    return property.toLowerCase().includes('status');
  }

  statusClass(value: string): string {
    const normalized = value.toLowerCase();
    if (['active', 'healthy', 'installed', 'up', 'enabled'].some(status => normalized === status)) return 'status-good';
    if (['inactive', 'maintenance', 'pending', 'staging'].some(status => normalized.includes(status))) return 'status-warning';
    if (['retired', 'down', 'failed', 'error', 'deleted'].some(status => normalized.includes(status))) return 'status-danger';
    return 'status-neutral';
  }

  private stateFor(tab: AssetTab): AssetListState {
    return tab === 'system' ? this.systemState : this.dataState;
  }

  private normalizeResponse(raw: unknown): NormalizedAssetResponse {
    // Case 1: API returns an array directly.
    if (Array.isArray(raw)) {
      const rows = raw.filter(
        (item): item is AssetManagement =>
          item !== null && typeof item === 'object'
      );

      return {
        rows,
        metadata: this.findMetadata(rows)
      };
    }

    // Case 2: API returns an object containing an array.
    if (raw !== null && typeof raw === 'object') {
      const wrapper = raw as Record<string, unknown>;

      // Include assetview because this is the key in your actual API response.
      const candidateKeys = [
        'assetview',
        'data',
        'items',
        'content',
        'results',
        'assets',
        'records'
      ];

      for (const key of candidateKeys) {
        const value = wrapper[key];

        if (Array.isArray(value)) {
          const rows = value.filter(
            (item): item is AssetManagement =>
              item !== null && typeof item === 'object'
          );

          const explicitMetadata = wrapper['metadata'] ?? wrapper['meta'];
          const paginationKeys = [
            'totalSystemAssets',
            'totalDataAssets',
            'totalAssets',
            'totalPages',
            'currentPage'
          ];
          const hasTopLevelPagination = paginationKeys.some(
            key => wrapper[key] !== undefined && wrapper[key] !== null
          );

          return {
            rows,
            metadata: (
              explicitMetadata ??
              (hasTopLevelPagination ? wrapper : this.findMetadata(rows))
            ) as AssetPaginationMeta | undefined
          };
        }
      }

      // Case 3: API returns a single asset object.
      if (
        wrapper['systemAssetId'] ||
        wrapper['assetId'] ||
        wrapper['name'] ||
        wrapper['systemAssetName']
      ) {
        const rows = [raw as AssetManagement];

        return {
          rows,
          metadata: this.findMetadata(rows)
        };
      }
    }

    // Case 4: Unexpected or unsupported response structure.
    console.warn(
      '[AssetsView] Unrecognized API response structure:',
      raw
    );

    return {
      rows: [],
      metadata: {}
    };
  }

  private findMetadata(rows: AssetManagement[]): AssetPaginationMeta | undefined {
    const record = rows.find(row => {
      const candidate = row as AssetRecord;
      return candidate['totalPages'] !== undefined ||
        candidate['totalSystemAssets'] !== undefined ||
        candidate['totalDataAssets'] !== undefined ||
        candidate['currentPage'] !== undefined;
    });
    return record as AssetPaginationMeta | undefined;
  }

  private formatClassification(value: unknown): string {
    if (Array.isArray(value)) return value.map(item => String(item)).join(', ');
    const text = String(value).trim();
    if (text.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(text);
        if (Array.isArray(parsed)) return parsed.map(item => String(item)).join(', ') || '—';
      } catch {
        // Retain the raw server string if it is not valid JSON.
      }
    }
    return text;
  }

  private readNonNegativeNumber(value: unknown, fallback: number): number {
    if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) {
      return fallback;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
  }

  private refreshView(): void {
    // Ensures the table state is rendered even if a custom interceptor resolves
    // an Observable-backed Promise outside Angular's normal change-detection turn.
    this.changeDetector.detectChanges();
  }

  /** Runs system asset verification, reports the result in a modal, and offers navigation on success. */
  async verifySystemAssets(): Promise<void> {
    if (this.isVerifying) return;

    this.isVerifying = true;
    this.verificationSucceeded = false;
    this.verificationMessage = '';

    try {
      const response = await this.assetsViewService.VerifySystemAssets().toPromise();
      this.verificationSucceeded = true;
      this.verificationMessage = this.extractResponseMessage(response)
        ?? 'System asset verification completed successfully.';
      this.showVerificationResultDialog();
    } catch (error: unknown) {
      this.verificationSucceeded = false;
      this.verificationMessage = this.extractResponseMessage(error)
        ?? 'System asset verification failed. Please check the connection and try again.';
      this.showVerificationResultDialog();
    } finally {
      this.isVerifying = false;
      this.refreshView();
    }
  }

  private showVerificationResultDialog(): void {
    this.verificationDialogRef = this.dialog.open<unknown, unknown, unknown>(
      this.verificationResultDialog,
      {
        width: '440px',
        maxWidth: 'calc(100vw - 2rem)',
        autoFocus: false,
        restoreFocus: true,
        disableClose: true,
        panelClass: 'verification-result-dialog-panel'
      }
    );

    this.verificationDialogRef.afterClosed().subscribe(() => {
      this.verificationDialogRef = null;
      if (this.verificationSucceeded) {
        this.openVerificationResults();
      }
    });
  }

  closeVerificationResult(): void {
    this.verificationDialogRef?.close();
  }

  openVerificationResults(): void {
    void this.router.navigate([
      '/asset-management/auth-tools/assets-view/system-assets-verification-view'
    ]);
  }

  /** Extracts a useful message from success payloads and HttpErrorResponse objects. */
  private extractResponseMessage(value: unknown, depth = 0): string | null {
    if (depth > 3 || value === null || value === undefined) return null;

    if (typeof value === 'string') {
      const message = value.trim();
      return message || null;
    }

    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      // Prefer a nested backend error payload over the generic HttpErrorResponse.message.
      const keys = ['error', 'message', 'detail', 'description', 'errorMessage'];
      for (const key of keys) {
        const candidate = record[key];
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate.trim();
        }
        if (candidate !== null && typeof candidate === 'object') {
          const nestedMessage = this.extractResponseMessage(candidate, depth + 1);
          if (nestedMessage) return nestedMessage;
        }
      }
    }

    if (value instanceof Error) {
      const message = value.message.trim();
      return message || null;
    }

    return null;
  }
}
